import React from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

import { Target, GitBranch, Settings } from "lucide-react";
import type { SilaeFormData, StructureType } from "@/types/silae";
import type { FormikProps } from "formik";
import { StructureSchema } from "../StructureSchema";
import { ExclusionConfig } from "@/components/silae/ExclusionConfig";

interface Step1StructureChoiceProps {
  formik: FormikProps<SilaeFormData>;
}

interface StructureOption {
  type: StructureType;
  icon: React.ReactNode;
  title: string;
  description: string;
  example: string;
  steps: number;
}

// Structure options will be created inside the component to use translations
// Plus besoin des types de primes guidées

/**
 * Étape 1 : Choix de la structure du code SILAE
 */
export const Step1StructureChoice: React.FC<Step1StructureChoiceProps> = ({
  formik,
}) => {
  const { t } = useTranslation();

  // Structure options with translations
  const structureOptions: StructureOption[] = [
    {
      type: "simple",
      icon: <Target className="h-6 w-6" />,
      title: t("wizard.structure.options.simple.title"),
      description: t("wizard.structure.options.simple.description"),
      example: t("wizard.structure.options.simple.example"),
      steps: 3,
    },
    {
      type: "with_conditions",
      icon: <Settings className="h-6 w-6" />,
      title: t("wizard.structure.options.with_conditions.title"),
      description: t("wizard.structure.options.with_conditions.description"),
      example: t("wizard.structure.options.with_conditions.example"),
      steps: 4,
    },
    {
      type: "if_else",
      icon: <GitBranch className="h-6 w-6" />,
      title: t("wizard.structure.options.if_else.title"),
      description: t("wizard.structure.options.if_else.description"),
      example: t("wizard.structure.options.if_else.example"),
      steps: 5,
    },
  ];

  const handleStructureChange = (structure: StructureType) => {
    formik.setFieldValue("structureType", structure);
  };

  return (
    <div className="space-y-6">
      {/* Configuration des exclusions */}
      <ExclusionConfig formik={formik} />
      {/* Structure du code SILAE */}
      <Card className="h-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-foreground">
            <GitBranch className="h-5 w-5" />
            {t("wizard.structure.cardTitle")}
          </CardTitle>
          <CardDescription>
            {t("wizard.structure.cardDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={formik.values.structureType}
            onValueChange={handleStructureChange}
            className="grid grid-cols-1 lg:grid-cols-3 gap-4"
          >
            {structureOptions.map((option) => (
              <Label
                key={option.type}
                htmlFor={option.type}
                className={`
                  flex flex-col space-y-3 p-4 bg-background dark:bg-background rounded-lg border-2 cursor-pointer transition-all h-full
                  ${
                    formik.values.structureType === option.type
                      ? "border-primary bg-primary/5 dark:bg-primary/5"
                      : "border-muted hover:border-muted-foreground/25"
                  }
                `}
              >
                <div className="flex items-center gap-2 justify-between w-full">
                  <div className="flex items-center gap-2 justify-between self-start">
                    <div className="flex items-center gap-3 ">
                      <RadioGroupItem value={option.type} id={option.type} />
                      {/* <div className="text-primary">{option.icon}</div> */}
                    </div>
                    <h3 className={`font-medium`}>{option.title}</h3>
                  </div>
                  <div className="flex items-center justify-between text-sm self-center">
                    <span className="text-muted-foreground/60">
                      {option.steps}{" "}
                      {t("wizard.structure.options.simple.steps").split(" ")[1]}
                    </span>
                  </div>
                </div>

                <div className="flex-1 w-full flex flex-col">
                  <p className="text-sm text-muted-foreground mt-1">
                    {option.description}
                  </p>

                  {/* Schéma visuel */}
                  <div className="mt-3 flex-grow border border-neutral-200 dark:border-neutral-700 rounded-lg bg-background dark:bg-background">
                    <StructureSchema type={option.type} />
                  </div>

                  <div className="mt-3 p-2 bg-muted rounded text-xs border border-neutral-200 dark:border-neutral-700">
                    <strong>{t("wizard.structure.example")}</strong>{" "}
                    {option.example}
                  </div>
                </div>
              </Label>
            ))}
          </RadioGroup>
        </CardContent>
      </Card>
    </div>
  );
};
